import PropTypes from 'prop-types';
import React, {useState, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {Divider} from '@material-ui/core';
import {debounce} from 'throttle-debounce';
import {convertToObjectByKey} from '@ozgene/gp5.utils.array-functions';
import {getUser} from 'UserLobby/service/UserService';
import ImportedProjectList from 'ImportedProjectLobby/components/ImportedProjectList';
import Loader from 'util/Loader';
import ImportedProjectLobbyActionToolbar from 'ImportedProjectLobby/components/ImportedProjectLobbyActionToolbar';
import {findAllImportedProjects} from 'Projects/service/ProjectService';

export default function ImportedProjectLobbyView(props, context) {
  const {router} = context;
  const {
    location: {
      query: {projectFilterText = '', userFilterText = ''},
    },
  } = props;
  const decodedProjectFilterText = decodeURIComponent(projectFilterText);
  const decodedUserFilterText = decodeURIComponent(userFilterText);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState({firstName: 'Unknown', lastName: 'User', authorities: []});
  const [selectedProjectId, setSelectedProjectId] = useState();
  const projectsById = useMemo(() => convertToObjectByKey(projects, 'id'), [projects]);
  const selectedProject = useMemo(
    () => projectsById[selectedProjectId],
    [selectedProjectId, projectsById],
  );
  const isAdmin = useMemo(
    () => user.authorities.some((auth) => auth.name === 'ROLE_ADMIN'),
    [user],
  );
  const handleCustomiseProject = () =>
    router.push({pathname: `/CustomiseProject/${selectedProject.id}`});
  const handleViewTimeline = () => router.push({pathname: `/Projects/${selectedProject.id}`});
  const handleSelectionChange = (selection) => {
    if (!!selection && selection.length > 0) {
      setSelectedProjectId(selection[selection.length - 1]);
    } else {
      setSelectedProjectId();
    }
  };
  const handleChangeProjectFilterText = debounce(1000, (filterText) => {
    const {
      location: {pathname, query: oldQuery},
    } = props;
    const query = {...oldQuery};

    if (!!filterText) {
      query.projectFilterText = encodeURIComponent(filterText);
    } else {
      delete query.projectFilterText;
    }

    router.replace({pathname, query});
  });
  const handleChangeUserFilterText = debounce(1000, (filterText) => {
    const {
      location: {pathname, query: oldQuery},
    } = props;
    const query = {...oldQuery};

    if (!!filterText) {
      query.userFilterText = encodeURIComponent(filterText);
    } else {
      delete query.userFilterText;
    }

    router.replace({pathname, query});
  });

  useEffect(() => {
    const handleRetrieveUser = async () => {
      const user = await getUser();

      setUser(user);
    };
    const handleRetrieveProjects = async () => {
      const importedProjects = await findAllImportedProjects();

      setProjects(importedProjects);
      setIsLoading(false);
    };

    if (isLoading) {
      handleRetrieveUser();
      handleRetrieveProjects();
    }
  }, [isLoading]);
  useEffect(() => {
    if (!isAdmin && selectedProject) {
      handleViewTimeline();
    }
  }, [selectedProject, isAdmin]);

  return (
    <>
      <Helmet title="Projects" defer={false} />
      <ImportedProjectLobbyActionToolbar
        projectSelected={!!selectedProject}
        onCustomiseProject={handleCustomiseProject}
        onViewTimelines={handleViewTimeline}
        numProjects={projects.length}
        isAdmin={isAdmin}
      />
      <Divider />
      {isLoading ? (
        <Loader label="Loading Projects..." />
      ) : (
        <ImportedProjectList
          projects={projects}
          selectedId={selectedProjectId}
          onSelectionChange={handleSelectionChange}
          onViewTimeline={handleViewTimeline}
          onCustomiseProject={handleCustomiseProject}
          onChangeProjectFilterText={handleChangeProjectFilterText}
          onChangeUserFilterText={handleChangeUserFilterText}
          projectFilterText={decodedProjectFilterText}
          userFilterText={decodedUserFilterText}
        />
      )}
    </>
  );
}

ImportedProjectLobbyView.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    query: PropTypes.shape({
      projectFilterText: PropTypes.string,
      userFilterText: PropTypes.string,
    }),
  }).isRequired,
};

ImportedProjectLobbyView.contextTypes = {
  router: PropTypes.object.isRequired,
};
